import {ChangeEvent, HTMLAttributes, useState} from 'react';
import styled from 'styled-components';
import {USER_PURPOSE_OPTIONS} from '@/constants';
import {UserProfileType, UserPurposeType} from '@/enums';
import {ColumnFlexBox, FlexFullGrow} from '@/styled-components';
import {palette} from '@/styles';
import {Button} from '../Button';
import {Checkbox, CheckboxItem, CheckboxLabel} from '../Checkbox';
import {TextField} from '../TextField';
import {Typography} from '../Typography';
import {InputContainer} from './StyledComponents';

interface IProps extends HTMLAttributes<HTMLDivElement> {
  defaultValues?: {[key: string]: any};
  onComplete(value: {[key: string]: any}): void;
}

const MIN_OTHER_COUNT = 2;
const MAX_OTHER_COUNT = 500;

export const UserPurposeStep = ({
  defaultValues,
  onComplete,
  ...props
}: IProps) => {
  const [values, setValues] = useState<{[key: string]: any}>(
    defaultValues || {}
  );

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const {checked, name} = e.target;
    const {type} = e.target.dataset;

    if (type) {
      setValues((prev) => {
        const next = {...prev};

        if (checked) {
          if (!next[type]) {
            next[type] = [];
          }

          if (!next[type].includes(name)) {
            next[type].push(name);
          }
        } else {
          if (name === UserPurposeType.Other) {
            next[UserProfileType.other] = undefined;
          }
          next[type] = next[type].filter((x: any) => x !== name);
        }

        return next;
      });
    }
  };

  const onTextFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const {value, name} = e.target;

    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onClick = () => {
    onComplete(values);
  };

  const purpose = values[UserProfileType.purpose];
  const showTextfield = purpose && purpose.includes(UserPurposeType.Other);
  const isValid =
    purpose?.length &&
    (!showTextfield || values[UserProfileType.other]?.length > MIN_OTHER_COUNT);

  return (
    <Form {...props}>
      {USER_PURPOSE_OPTIONS.map((i) => (
        <Item key={i.id} gap="8px">
          <Checkbox
            checked={purpose?.includes(i.id)}
            name={i.id}
            data-type={UserProfileType.purpose}
            variant="large"
            checkType="circle"
            onChange={onChange}
          />
          <CheckboxLabel>
            {i.label}
            {!!i.hint?.length && (
              <Typography variant="medium14" color={palette.gray600}>
                {' '}
                ({i.hint})
              </Typography>
            )}
          </CheckboxLabel>
        </Item>
      ))}
      {showTextfield && (
        <ColumnFlexBox gap={4}>
          <TextField
            variant="large"
            fullWidth
            placeholder="서비스를 이용하시는 다른 목적을 알려주세요."
            maxLength={MAX_OTHER_COUNT}
            name={UserProfileType.other}
            value={values[UserProfileType.other]}
            height={44}
            fontSize={16}
            onChange={onTextFieldChange}
          />
          <Typography
            as="div"
            variant="paragraph12"
            lineHeight="20px"
            color={palette.gray600}
          >
            최소 {MIN_OTHER_COUNT}자~ 최대 {MAX_OTHER_COUNT}자
          </Typography>
        </ColumnFlexBox>
      )}
      <FlexFullGrow />
      <Button
        disabled={!isValid}
        variant="fill"
        color="primary"
        size="mediumLarge"
        fullWidth
        onClick={onClick}
      >
        다음
      </Button>
    </Form>
  );
};

const Form = styled(InputContainer)``;

const Item = styled(CheckboxItem)`
  padding: 8px 40px 8px 0;
  ${CheckboxLabel} {
    color: ${palette.gray800};
    font-size: 16px;
    font-weight: 400;
    line-height: 1.5;
    letter-spacing: -0.03px;
  }
`;
